<script setup lang="ts">
import { DatePicker } from 'v-calendar';
import { computed, ref } from 'vue';
import Papa from 'papaparse';
import kebabCase from 'lodash/kebabCase';
import { Client } from '@/models/Client';
import DateInput from '@/components/DateInput.vue';
import AppButton from '@/components/AppButton.vue';
import { supabase } from '@/lib/supabase';
import { downloadCsv } from '@/utilities/Helpers';

const props = defineProps<{
	client: Client;
}>();

const from = ref();
const till = ref();

const isDownloadButtonDisabled = computed(() => !from.value);

const onDownloadCSV = async () => {
	const { data } = await supabase
		.rpc('get_client_notes', {
			p_client_id: props.client.id,
			p_from: from.value,
			p_till: till.value ?? null,
		});

	if (!data) {
		return;
	}

	const csvString = Papa.unparse(data);
	downloadCsv(csvString, `${kebabCase(props.client.name)}-export.csv`);
};
</script>

<template>
	<div>
		<div class="text-2xl font-bold mt-10">
			{{ client.name }}
		</div>
		<div class="flex gap-4 mt-8">
			<div>
				<div class="col-span-3">
					<div class="text-xs text-gray">
						{{ $t('common.startDate') }}
					</div>
				</div>

				<DatePicker
					v-model="from"
					class="custom-datepicker"
					mode="date"
					:popover="{ visibility: 'focus' }"
				>
					<template #default="{ inputValue, inputEvents }">
						<DateInput
							size="md"
							:model-value="inputValue"
							placeholder="dd.mm.yyyy"
							v-on="inputEvents"
						/>
					</template>
				</DatePicker>
			</div>

			<div>
				<div class="col-span-3">
					<div class="text-xs text-gray">
						{{ $t('common.endDateOptional') }}
					</div>
				</div>

				<DatePicker
					v-model="till"
					class="custom-datepicker"
					mode="date"
					:popover="{ visibility: 'focus' }"
				>
					<template #default="{ inputValue, inputEvents }">
						<DateInput
							size="md"
							:model-value="inputValue"
							placeholder="dd.mm.yyyy"
							v-on="inputEvents"
						/>
					</template>
				</DatePicker>
			</div>
		</div>

		<div class="flex justify-center mt-6">
			<AppButton
				:disabled="isDownloadButtonDisabled"
				@click="onDownloadCSV"
			>
				{{ $t('common.downloadCSV') }}
			</AppButton>
		</div>
	</div>
</template>
